import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { editScript, listVoices, previewVoiceover, updateSceneModes } from "@/lib/api";
import NarrationDiff from "./NarrationDiff";
import SceneModePicker, { SCENE_MODE_LABELS } from "./SceneModePicker";

/**
 * ScriptEditor — lets the user edit narration / title / per-scene visual
 * prompts / voice before (or after) a render. On save, voiceover is
 * regenerated server-side and the job is flagged `video_stale` so the
 * user can click "Re-render" to spend Veo credits on a fresh MP4.
 *
 * Disabled when status or video_status === "running" so the user can't
 * mutate the job mid-render.
 */
export default function ScriptEditor({ doc, jobId, onChanged }) {
  const result = doc?.result || {};
  const script = result.script || {};
  const voiceover = result.voiceover || {};
  const isRunning = doc?.status === "running" || doc?.video_status === "running";

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [narration, setNarration] = useState("");
  const [scenes, setScenes] = useState([]);
  const [sceneModes, setSceneModes] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [voices, setVoices] = useState({ builtin: [], custom: [], resemble: [] });
  // Narration pace (ElevenLabs `speed` voice_setting). 1.0 = normal,
  // 0.85 = slower for kids, 1.10 = punchier for shorts. Slider snaps to
  // these presets via the buttons above it.
  const [speed, setSpeed] = useState(1.0);
  const [saving, setSaving] = useState(false);
  // A/B Preview state — fetch + play a ~5s sample of the chosen
  // voice + speed without committing to a full regen.
  const [previewing, setPreviewing] = useState(false);
  const previewAudioRef = useRef(null);
  // Karaoke state: char index in the ORIGINAL narration text currently
  // being spoken. Drives the highlight overlay rendered below the
  // textarea while a marker preview plays. null = not playing.
  const [karaokeIdx, setKaraokeIdx] = useState(null);
  // Which scene (1-indexed) is currently being previewed by the per-cut
  // "▶ preview cut" buttons. null = idle. Used to flip the matching
  // button into a "■ stop" state and gray out the others.
  const [scenePreviewIdx, setScenePreviewIdx] = useState(null);
  // Smart cache for the marker preview. Stores the last (narration +
  // voice + speed) tuple alongside the ElevenLabs response so a repeat
  // click — common while iterating marker positions — reuses the
  // existing MP3 + alignment instead of paying for another TTS call.
  const markerPreviewCacheRef = useRef(null);
  // Pace autosave indicator: "idle" | "saving" | "saved" | "error".
  // Drives the small "⚡ Synced" badge next to the Pace slider so the
  // user gets visual confirmation that the slider value persisted.
  const [paceSyncState, setPaceSyncState] = useState("idle");
  const narrationRef = useRef(null);

  // Hydrate local state from the doc whenever the editor is opened OR the
  // underlying script changes. We deliberately don't keep local edits
  // when the doc updates from polling — anything mid-edit would clobber.
  useEffect(() => {
    if (!open) return;
    setTitle(script.title || "");
    setNarration(script.narration || "");
    const sc = Array.isArray(script.scene_breakdown) ? script.scene_breakdown : [];
    setScenes(sc.length ? sc.map((s) => String(s || "")) : [""]);
    // Hydrate per-scene composition modes. Backend default = "still_static"
    // when nothing has been set yet, matching the user's confirmed default.
    const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
    const seedModes = (sc.length ? sc : [""]).map((_, i) =>
      docModes[i] && SCENE_MODE_LABELS[docModes[i]] ? docModes[i] : "still_static",
    );
    setSceneModes(seedModes);
    setVoiceId(voiceover.voice_id || "");
    // Hydrate pace. Fall back to 1.0 (normal) if the job predates the
    // Pace control. Clamp to range so a corrupted doc doesn't break the slider.
    const rawSpeed = Number(voiceover.speed);
    setSpeed(
      Number.isFinite(rawSpeed) ? Math.max(0.7, Math.min(1.2, rawSpeed)) : 1.0,
    );
  // We only want hydration to fire on the open transition, not on every
  // doc poll — otherwise the user's in-progress edits would get reset
  // every 2 seconds when polling returns fresh state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // One-shot voice catalog load on mount.
  useEffect(() => {
    let alive = true;
    listVoices()
      .then((v) => { if (alive) setVoices(v); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Words-per-scene estimate so the user can eyeball pacing.
  const sceneWordTargets = useMemo(() => {
    if (!scenes.length || !narration) return [];
    const totalWords = narration.split(/\s+/).filter(Boolean).length;
    const perScene = Math.round(totalWords / scenes.length);
    return scenes.map(() => perScene);
  }, [scenes.length, narration]);

  const updateScene = (idx, value) => {
    setScenes((prev) => prev.map((s, i) => (i === idx ? value : s)));
  };
  const updateSceneMode = (idx, mode) => {
    setSceneModes((prev) => prev.map((m, i) => (i === idx ? mode : m)));
  };
  const addScene = () => {
    setScenes((prev) => [...prev, ""]);
    setSceneModes((prev) => [...prev, "still_static"]);
  };
  const removeScene = (idx) => {
    setScenes((prev) => prev.filter((_, i) => i !== idx));
    setSceneModes((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!narration.trim()) {
      toast.error("Narration can't be empty.");
      return;
    }
    const cleanScenes = scenes
      .map((s) => (s || "").trim())
      .filter((s) => s.length > 0);
    if (cleanScenes.length === 0) {
      toast.error("Need at least one scene description.");
      return;
    }
    // Trim sceneModes to match the cleaned scene list (drop modes for
    // removed-blank scenes from the tail).
    const cleanModes = sceneModes.slice(0, cleanScenes.length);
    while (cleanModes.length < cleanScenes.length) cleanModes.push("still_static");
    setSaving(true);
    const toastId = toast.loading("Saving + re-generating voiceover…", {
      description: "ElevenLabs is reading the new narration — ~10-30s.",
    });
    try {
      const res = await editScript(jobId, {
        title: title.trim() || undefined,
        narration: narration.trim(),
        scene_breakdown: cleanScenes,
        voice_id: voiceId || undefined,
        speed,
      });
      // Persist scene_modes in a second call (separate endpoint so the
      // surgical clip invalidation only runs when modes actually change).
      let modesResp = null;
      try {
        modesResp = await updateSceneModes(jobId, cleanModes);
      } catch (modeErr) {
        // Non-fatal — script saved, modes update can be retried.
        toast.error(modeErr?.response?.data?.detail || "Couldn't save scene modes");
      }
      if (res.voiceover_regenerated) {
        toast.success("Saved — new voiceover ready. Click Re-render to refresh the video.", {
          id: toastId, duration: 6000,
        });
      } else if (modesResp && modesResp.changed_indices?.length) {
        toast.success(
          `Saved — ${modesResp.changed_indices.length} scene mode(s) updated. Click Re-render.`,
          { id: toastId, duration: 6000 },
        );
      } else {
        toast.success("Saved.", { id: toastId });
      }
      setOpen(false);
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save edits", {
        id: toastId,
      });
    } finally {
      setSaving(false);
    }
  };

  const insertPause = (seconds) => {
    const tag = `<break time="${Math.min(3, Math.max(0.1, seconds))}s" /> `;
    _insertAtCursor(tag);
  };

  const insertSceneMarker = () => {
    // [[scene]] marker — backend strips it before TTS and uses
    // /with-timestamps to align scene transitions to this exact spot.
    _insertAtCursor("[[scene]] ");
  };

  const _insertAtCursor = (insert) => {
    const ta = narrationRef.current;
    if (!ta) {
      setNarration((n) => (n ? `${n} ${insert}` : insert));
      return;
    }
    const start = ta.selectionStart ?? narration.length;
    const end = ta.selectionEnd ?? narration.length;
    const before = narration.slice(0, start);
    const after = narration.slice(end);
    const next = `${before}${insert}${after}`;
    setNarration(next);
    const newPos = start + insert.length;
    requestAnimationFrame(() => {
      try {
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      } catch { /* noop */ }
    });
  };

  const handlePreview = async () => {
    if (!voiceId) {
      toast.error("Pick a voice first");
      return;
    }
    // Cut any preview that's already playing — user is iterating fast.
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* noop */ }
      previewAudioRef.current = null;
    }
    setPreviewing(true);
    const toastId = toast.loading("Generating 5s preview…");
    try {
      const { data_url } = await previewVoiceover({
        voice_id: voiceId,
        speed,
      });
      const audio = new Audio(data_url);
      previewAudioRef.current = audio;
      audio.onended = () => { setPreviewing(false); previewAudioRef.current = null; };
      audio.onerror = () => {
        setPreviewing(false);
        previewAudioRef.current = null;
        toast.error("Preview playback failed", { id: toastId });
      };
      await audio.play();
      toast.success(`Playing — ${speed.toFixed(2)}× pace`, { id: toastId, duration: 2500 });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't generate preview",
        { id: toastId },
      );
      setPreviewing(false);
    }
  };

  // Shared helper: fetch (or reuse from cache) the full-narration MP3 +
  // character-level alignment from ElevenLabs. Used by both the
  // "▶ play with markers" karaoke playback AND the per-scene
  // "▶ preview cut" buttons. Returns `cacheHit` so the caller can tweak
  // its toast copy. Throws on backend / network failure.
  const _ensureAlignment = async () => {
    const cacheKey = `${voiceId}|${speed.toFixed(3)}|${narration}`;
    const cached = markerPreviewCacheRef.current;
    if (cached && cached.key === cacheKey) {
      return { ...cached, cacheHit: true };
    }
    const resp = await previewVoiceover({
      voice_id: voiceId,
      speed,
      text: narration,
      with_alignment: true,
    });
    // Log the diagnostic fields so the user can verify prod has the
    // speed-scaling fix. If mp3_duration_sec is FAST at slow pace, the
    // backend is missing the fix and a redeploy is needed.
    // eslint-disable-next-line no-console
    console.log(
      "[play-with-markers] backend response →",
      {
        echoed_speed: resp.speed,
        mp3_duration_sec: resp.mp3_duration_sec,
        tts_path: resp.tts_path,
        size_bytes: resp.size_bytes,
        marker_count: (resp.marker_timestamps_sec || []).length,
      },
    );
    const entry = {
      key: cacheKey,
      data_url: resp.data_url,
      char_start_times_sec: resp.char_start_times_sec,
      marker_positions_orig: resp.marker_positions_orig,
      marker_timestamps_sec: resp.marker_timestamps_sec || [],
      mp3_duration_sec: resp.mp3_duration_sec,
      tts_path: resp.tts_path,
    };
    markerPreviewCacheRef.current = entry;
    return { ...entry, cacheHit: false };
  };

  const handlePlayWithMarkers = async () => {
    if (!voiceId) {
      toast.error("Pick a voice first");
      return;
    }
    if (!narration.includes("[[scene]]")) {
      toast.error("Drop at least one ▸ scene cut marker first");
      return;
    }
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* noop */ }
      previewAudioRef.current = null;
    }
    setPreviewing(true);
    const toastId = toast.loading("Generating preview + alignment…");
    try {
      const {
        data_url,
        char_start_times_sec,
        marker_positions_orig,
        cacheHit,
      } = await _ensureAlignment();
      // Build a position-mapping array so each char index of the CLEAN
      // text knows its corresponding index in the ORIGINAL (with-marker)
      // text. As the audio plays through clean index N, we set the
      // textarea caret to original index `origIdxForCleanIdx[N]`.
      const markerLen = "[[scene]] ".length; // marker token + trailing space we eat
      const origIdxForCleanIdx = (() => {
        const arr = [];
        let origIdx = 0;
        let cleanIdx = 0;
        let markerSeen = 0;
        const sorted = [...marker_positions_orig].sort((a, b) => a - b);
        while (cleanIdx <= char_start_times_sec.length) {
          // If origIdx lands on the next marker, skip past it.
          while (
            markerSeen < sorted.length &&
            origIdx === sorted[markerSeen]
          ) {
            origIdx += markerLen;
            markerSeen += 1;
          }
          arr.push(origIdx);
          origIdx += 1;
          cleanIdx += 1;
        }
        return arr;
      })();

      const audio = new Audio(data_url);
      previewAudioRef.current = audio;
      const ta = narrationRef.current;
      audio.ontimeupdate = () => {
        if (!ta) return;
        const t = audio.currentTime;
        // Binary search the alignment for the largest index whose
        // start_time ≤ currentTime.
        let lo = 0, hi = char_start_times_sec.length - 1, idx = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (char_start_times_sec[mid] <= t) { idx = mid; lo = mid + 1; }
          else { hi = mid - 1; }
        }
        const origIdx = origIdxForCleanIdx[idx] ?? 0;
        try {
          ta.focus({ preventScroll: false });
          ta.setSelectionRange(origIdx, origIdx);
        } catch { /* noop */ }
        setKaraokeIdx(origIdx);
      };
      audio.onended = () => {
        setPreviewing(false);
        previewAudioRef.current = null;
        setKaraokeIdx(null);
      };
      audio.onerror = () => {
        setPreviewing(false);
        previewAudioRef.current = null;
        setKaraokeIdx(null);
        toast.error("Preview playback failed", { id: toastId });
      };
      await audio.play();
      // Surface the MP3 duration in the toast so the user can SEE
      // whether the backend produced a slow or fast MP3 without
      // opening devtools. At speed=0.75, expect ~1.3× the duration of
      // speed=1.0; if the value looks identical regardless of pace,
      // production is missing the speed-scaling fix.
      const cached = markerPreviewCacheRef.current;
      const dur = cached?.mp3_duration_sec;
      const ttsPath = cached?.tts_path;
      const tag = dur != null ? ` · mp3=${Number(dur).toFixed(1)}s @ ${speed.toFixed(2)}×` : "";
      const pathTag = ttsPath ? ` · ${ttsPath}` : "";
      toast.success(
        (cacheHit ? "Replaying (cached)" : "Playing with markers") + tag + pathTag,
        { id: toastId, duration: 5000 },
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't generate marker preview",
        { id: toastId },
      );
      setPreviewing(false);
    }
  };

  const handleStopPreview = () => {
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* noop */ }
      previewAudioRef.current = null;
    }
    setPreviewing(false);
    setKaraokeIdx(null);
    setScenePreviewIdx(null);
  };

  // Play ONE scene's audio segment in isolation, between
  // `boundaries[idx]` and `boundaries[idx + 1]`. Lets the user fine-tune
  // marker placement without scrubbing the full timeline. Reuses the
  // shared alignment cache so repeat clicks across scenes only burn
  // ONE ElevenLabs call per (narration, voice, speed) tuple.
  //
  //   boundaries = [0, t1, t2, ..., tN-1, audio.duration]
  //   scene 1 plays [0, t1]; scene 2 plays [t1, t2]; etc.
  //
  // While the segment plays we drive the karaoke overlay + caret the
  // same way `handlePlayWithMarkers` does, so the user sees the exact
  // words that belong to this cut.
  const handlePreviewScene = async (sceneIdx) => {
    if (!voiceId) {
      toast.error("Pick a voice first");
      return;
    }
    // Toggle off if this same scene is already playing.
    if (scenePreviewIdx === sceneIdx + 1 && previewAudioRef.current) {
      handleStopPreview();
      return;
    }
    // Stop whatever else is playing first.
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* noop */ }
      previewAudioRef.current = null;
    }
    setPreviewing(true);
    setScenePreviewIdx(sceneIdx + 1);
    const toastId = toast.loading(`Loading scene ${sceneIdx + 1}…`);
    try {
      const {
        data_url,
        char_start_times_sec,
        marker_positions_orig,
        marker_timestamps_sec,
      } = await _ensureAlignment();
      // Same orig↔clean mapping the full-playback path uses.
      const markerLen = "[[scene]] ".length;
      const origIdxForCleanIdx = (() => {
        const arr = [];
        let origIdx = 0, cleanIdx = 0, markerSeen = 0;
        const sorted = [...marker_positions_orig].sort((a, b) => a - b);
        while (cleanIdx <= char_start_times_sec.length) {
          while (
            markerSeen < sorted.length &&
            origIdx === sorted[markerSeen]
          ) { origIdx += markerLen; markerSeen += 1; }
          arr.push(origIdx);
          origIdx += 1; cleanIdx += 1;
        }
        return arr;
      })();
      const audio = new Audio(data_url);
      previewAudioRef.current = audio;
      // Need duration before we can compute the last scene's end boundary.
      await new Promise((resolve, reject) => {
        if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
          resolve();
        } else {
          audio.addEventListener("loadedmetadata", resolve, { once: true });
          audio.addEventListener("error", reject, { once: true });
        }
      });
      const boundaries = [0, ...marker_timestamps_sec, audio.duration];
      const startSec = boundaries[sceneIdx] ?? 0;
      const endSec = boundaries[sceneIdx + 1] ?? audio.duration;
      if (!(endSec > startSec)) {
        throw new Error("Scene boundaries are inverted — re-check markers");
      }
      audio.currentTime = startSec;
      const ta = narrationRef.current;
      audio.ontimeupdate = () => {
        const t = audio.currentTime;
        // Auto-stop at the next marker. Pause + fire onended so cleanup runs.
        if (t >= endSec) {
          try { audio.pause(); } catch { /* noop */ }
          audio.dispatchEvent(new Event("ended"));
          return;
        }
        if (!ta) return;
        let lo = 0, hi = char_start_times_sec.length - 1, idx = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (char_start_times_sec[mid] <= t) { idx = mid; lo = mid + 1; }
          else { hi = mid - 1; }
        }
        const origIdx = origIdxForCleanIdx[idx] ?? 0;
        try {
          ta.focus({ preventScroll: false });
          ta.setSelectionRange(origIdx, origIdx);
        } catch { /* noop */ }
        setKaraokeIdx(origIdx);
      };
      audio.onended = () => {
        setPreviewing(false);
        setScenePreviewIdx(null);
        previewAudioRef.current = null;
        setKaraokeIdx(null);
      };
      audio.onerror = () => {
        setPreviewing(false);
        setScenePreviewIdx(null);
        previewAudioRef.current = null;
        setKaraokeIdx(null);
        toast.error("Scene preview failed", { id: toastId });
      };
      await audio.play();
      toast.success(
        `Scene ${sceneIdx + 1} · ${(endSec - startSec).toFixed(1)}s`,
        { id: toastId, duration: 2500 },
      );
    } catch (err) {
      setPreviewing(false);
      setScenePreviewIdx(null);
      toast.error(
        err?.response?.data?.detail || err?.message || "Couldn't preview scene",
        { id: toastId },
      );
    }
  };

  // Drop the marker-preview cache whenever the editor closes — fresh
  // session, fresh state. Cheap; the cache will refill on the next play.
  useEffect(() => {
    if (!open) markerPreviewCacheRef.current = null;
  }, [open]);

  // Auto-persist the Pace slider value to the job doc (with
  // skip_regen=true so we don't burn an ElevenLabs call on every tick).
  // This ensures the standalone "Regenerate voiceover" button outside
  // the editor ALWAYS reads the latest pace from the doc — fixes the
  // "preview sounds slow but regen still sounds fast" bug.
  useEffect(() => {
    if (!open || !jobId) return;
    const docSpeed = Number.isFinite(Number(voiceover?.speed))
      ? Number(voiceover.speed)
      : 1.0;
    if (Math.abs(speed - docSpeed) < 0.001) return;
    setPaceSyncState("saving");
    const t = setTimeout(async () => {
      try {
        await editScript(jobId, { speed, skip_regen: true });
        setPaceSyncState("saved");
        // Fade the green badge back to idle after 1.5s.
        setTimeout(() => setPaceSyncState("idle"), 1500);
      } catch {
        setPaceSyncState("error");
        setTimeout(() => setPaceSyncState("idle"), 2500);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [speed, open, jobId, voiceover?.speed]);

  // Tokenize the narration once per change so the karaoke overlay
  // doesn't recompute on every audio frame. Each token is a word, a
  // whitespace run, OR a `[[scene]]` marker — markers render as small
  // dimmed pills, words flip yellow when the narrator reaches them.
  const narrationTokens = useMemo(() => {
    if (!narration) return [];
    const tokens = [];
    let i = 0;
    const MARKER = "[[scene]]";
    while (i < narration.length) {
      if (narration.startsWith(MARKER, i)) {
        tokens.push({ kind: "marker", text: MARKER, start: i, end: i + MARKER.length });
        i += MARKER.length;
        continue;
      }
      // Whitespace run
      let j = i;
      while (j < narration.length && /\s/.test(narration[j])) j += 1;
      if (j > i) {
        tokens.push({ kind: "ws", text: narration.slice(i, j), start: i, end: j });
        i = j;
        continue;
      }
      // Word run — stop at whitespace OR the start of a `[[scene]]` marker
      j = i;
      while (
        j < narration.length &&
        !/\s/.test(narration[j]) &&
        !narration.startsWith(MARKER, j)
      ) j += 1;
      tokens.push({ kind: "word", text: narration.slice(i, j), start: i, end: j });
      i = j;
    }
    return tokens;
  }, [narration]);

  const isDirty = useMemo(() => {
    if (!open) return false;
    if ((title || "") !== (script.title || "")) return true;
    if ((narration || "") !== (script.narration || "")) return true;
    if ((voiceId || "") !== (voiceover.voice_id || "")) return true;
    // Pace dirty-check: treat missing job.speed as 1.0.
    const docSpeed = Number.isFinite(Number(voiceover.speed))
      ? Number(voiceover.speed)
      : 1.0;
    if (Math.abs(speed - docSpeed) > 0.001) return true;
    const cur = Array.isArray(script.scene_breakdown) ? script.scene_breakdown : [];
    if (scenes.length !== cur.length) return true;
    for (let i = 0; i < scenes.length; i += 1) {
      if ((scenes[i] || "") !== (cur[i] || "")) return true;
    }
    // Scene mode diff — any change vs the saved doc.scene_modes (treating
    // missing/invalid entries as "still_static" since that's the default).
    const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
    for (let i = 0; i < scenes.length; i += 1) {
      const docMode = SCENE_MODE_LABELS[docModes[i]] ? docModes[i] : "still_static";
      const localMode = sceneModes[i] || "still_static";
      if (localMode !== docMode) return true;
    }
    return false;
  }, [open, title, narration, voiceId, speed, scenes, sceneModes, script, voiceover, doc]);

  if (!script.narration) {
    return null;
  }

  return (
    <div
      className="border border-zinc-800 rounded-sm bg-black p-5 mt-4"
      data-testid="script-editor-card"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
          ✎ Script &amp; voice
        </div>
        <div className="flex items-center gap-2">
          {doc?.video_stale && !isRunning && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 border border-yellow-400/40 bg-yellow-400/5 px-2 py-1 rounded-sm"
              title="Voiceover was re-generated after the current video — re-render to refresh the MP4"
              data-testid="script-stale-badge"
            >
              ⚠ Video out of date
            </span>
          )}
          {!open ? (
            <button
              type="button"
              data-testid="script-edit-btn"
              onClick={() => setOpen(true)}
              disabled={isRunning}
              className="font-mono text-[10px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={isRunning ? "Wait for the current render to finish" : "Edit narration, scenes, and voice"}
            >
              ✎ Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                data-testid="script-cancel-btn"
                onClick={() => {
                  if (isDirty && !window.confirm("Discard your edits?")) return;
                  setOpen(false);
                }}
                disabled={saving}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border border-zinc-800 hover:text-red-300 hover:border-red-500/40 px-3 py-1.5 rounded-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="script-save-btn"
                onClick={save}
                disabled={saving || !isDirty}
                className="font-mono text-[10px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-wait inline-flex items-center gap-1.5"
              >
                {saving ? (
                  <>
                    <span className="inline-block w-2.5 h-2.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Saving…
                  </>
                ) : "✓ Save & re-tts"}
              </button>
            </>
          )}
        </div>
      </div>

      {!open ? (
        <ReadonlyScript script={script} voiceover={voiceover} />
      ) : (
        <div className="space-y-4">
          {/* Title + voice row */}
          <div className="grid sm:grid-cols-[1fr_280px] gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1.5">
                Title
              </label>
              <input
                data-testid="script-edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="YouTube title (≤120 chars)"
                maxLength={120}
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400 rounded-sm px-3 py-2 text-sm text-white outline-none transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1.5">
                Voice
              </label>
              <select
                data-testid="script-edit-voice"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400 rounded-sm px-3 py-2 text-sm text-white outline-none transition-colors"
              >
                {(voices?.builtin?.length || 0) + (voices?.custom?.length || 0) + (voices?.resemble?.length || 0) === 0 && (
                  <option value="">Loading voices…</option>
                )}
                {voices?.builtin?.length > 0 && (
                  <optgroup label="ElevenLabs · Built-in">
                    {voices.builtin.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.label}</option>
                    ))}
                  </optgroup>
                )}
                {voices?.custom?.length > 0 && (
                  <optgroup label="ElevenLabs · My custom voices">
                    {voices.custom.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.label}</option>
                    ))}
                  </optgroup>
                )}
                {voices?.resemble?.length > 0 && (
                  <optgroup label="Resemble AI · My voices">
                    {voices.resemble.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.label}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>

          {/* Pace (ElevenLabs `speed` voice_setting). Preset buttons + slider. */}
          <div data-testid="script-edit-pace-block">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5 flex justify-between items-end">
              <span className="flex items-center gap-2">
                <span>Pace — how fast the voice reads</span>
                {paceSyncState !== "idle" && (
                  <span
                    data-testid="pace-sync-badge"
                    className={[
                      "font-mono text-[9px] normal-case tracking-[0.15em] px-1.5 py-0.5 rounded-sm border transition-all",
                      paceSyncState === "saving"
                        ? "border-zinc-700 text-zinc-400 bg-zinc-900"
                        : paceSyncState === "saved"
                        ? "border-green-500/60 text-green-400 bg-green-500/10"
                        : "border-red-500/60 text-red-400 bg-red-500/10",
                    ].join(" ")}
                  >
                    {paceSyncState === "saving" && "⋯ saving"}
                    {paceSyncState === "saved" && "⚡ synced"}
                    {paceSyncState === "error" && "✕ save failed"}
                  </span>
                )}
              </span>
              <span className="text-zinc-500 normal-case tracking-normal">
                {speed.toFixed(2)}× ·{" "}
                {speed < 0.95 ? "slower (kids-friendly)" : speed > 1.05 ? "faster (punchy)" : "normal"}
              </span>
            </label>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {[
                { label: "Slow", value: 0.85, testid: "pace-preset-slow" },
                { label: "Normal", value: 1.0, testid: "pace-preset-normal" },
                { label: "Fast", value: 1.1, testid: "pace-preset-fast" },
              ].map((p) => {
                const active = Math.abs(speed - p.value) < 0.005;
                return (
                  <button
                    key={p.value}
                    type="button"
                    data-testid={p.testid}
                    onClick={() => setSpeed(p.value)}
                    className={[
                      "font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border transition-colors",
                      active
                        ? "bg-yellow-400 text-black border-yellow-400 font-bold"
                        : "bg-black text-zinc-300 border-zinc-800 hover:border-yellow-400 hover:text-yellow-400",
                    ].join(" ")}
                  >
                    {p.label} · {p.value.toFixed(2)}×
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewing || !voiceId}
                data-testid="pace-preview-btn"
                className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border border-zinc-700 text-zinc-200 hover:border-yellow-400 hover:text-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ml-auto"
                title="Generate a 5-second sample at the chosen voice + pace"
              >
                {previewing ? "▶ playing…" : "▶ hear 5 seconds"}
              </button>
            </div>
            <input
              type="range"
              data-testid="script-edit-pace-slider"
              min="0.7"
              max="1.2"
              step="0.05"
              value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              className="w-full accent-yellow-400"
            />
          </div>

          {/* Narration */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1.5 flex justify-between">
              <span>Narration — read top-to-bottom by the voice actor</span>
              <span className="text-zinc-500 normal-case tracking-normal">
                {narration.split(/\s+/).filter(Boolean).length} words ·
                {" "}≈{Math.round(narration.split(/\s+/).filter(Boolean).length / 2.5)}s
              </span>
            </label>
            {/* Pause-insert toolbar — drops <break time="Xs" /> at the
                cursor. ElevenLabs honors these tags as actual silence. */}
            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap" data-testid="narration-pause-toolbar">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mr-1">
                Insert pause at cursor ▸
              </span>
              {[0.5, 1, 1.5, 2].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`narration-pause-${s}s`}
                  onClick={() => insertPause(s)}
                  title={`Insert a ${s}-second pause at the cursor`}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-800 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 transition-colors"
                >
                  + {s}s pause
                </button>
              ))}
              <button
                type="button"
                data-testid="narration-scene-cut"
                onClick={() => insertSceneMarker()}
                title="Insert a scene-transition marker at the cursor. The next render will switch scenes exactly when the narrator reaches this point."
                className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-yellow-400/40 text-yellow-400 hover:border-yellow-400 hover:bg-yellow-400/10 transition-colors ml-2"
              >
                ▸ scene cut
              </button>
              {/* "Play with markers" — renders the full narration with
                  alignment and slides the textarea caret through it in
                  real time so the user can SEE the narrator pass each
                  [[scene]] marker before paying for a full render. */}
              {!previewing ? (
                <button
                  type="button"
                  data-testid="narration-play-with-markers"
                  onClick={handlePlayWithMarkers}
                  disabled={!voiceId || !narration.includes("[[scene]]")}
                  title="Play the full narration and slide the cursor through the markers so you can verify timing."
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ▶ play with markers
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="narration-stop-preview"
                  onClick={handleStopPreview}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-red-500/60 text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  ■ stop
                </button>
              )}
              {(() => {
                const markerCount = (narration.match(/\[\[scene\]\]/g) || []).length;
                const expected = Math.max(0, scenes.length - 1);
                const ok = markerCount === expected;
                if (markerCount === 0) {
                  return (
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 ml-1">
                      no sync · even split
                    </span>
                  );
                }
                return (
                  <span
                    data-testid="narration-marker-status"
                    className={
                      "font-mono text-[9px] uppercase tracking-[0.2em] ml-1 " +
                      (ok ? "text-green-400" : "text-orange-400")
                    }
                  >
                    {markerCount} marker{markerCount === 1 ? "" : "s"} ·{" "}
                    {ok ? `synced (${scenes.length} scenes)` : `need ${expected} for ${scenes.length} scenes`}
                  </span>
                );
              })()}
            </div>
            {/* Per-scene "▶ preview cut" row — one button per scene, shown
                only when the marker count exactly matches the scene count
                (i.e. fully-synced state). Lets the user audition each cut
                in isolation without scrubbing the full timeline. */}
            {(() => {
              const markerCount = (narration.match(/\[\[scene\]\]/g) || []).length;
              const expected = Math.max(0, scenes.length - 1);
              if (markerCount === 0 || markerCount !== expected || scenes.length < 2) {
                return null;
              }
              return (
                <div
                  data-testid="scene-preview-row"
                  className="flex items-center gap-1.5 mb-2 flex-wrap"
                >
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mr-1">
                    Audition cut ▸
                  </span>
                  {scenes.map((_, i) => {
                    const isPlaying = scenePreviewIdx === i + 1;
                    const otherPlaying = previewing && !isPlaying;
                    return (
                      <button
                        key={i}
                        type="button"
                        data-testid={`scene-preview-${i}`}
                        onClick={() => handlePreviewScene(i)}
                        disabled={!voiceId || otherPlaying}
                        title={`Hear just scene ${i + 1} of ${scenes.length}`}
                        className={[
                          "font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border transition-colors",
                          isPlaying
                            ? "border-red-500/60 text-red-400 bg-red-500/10 hover:bg-red-500/20"
                            : "border-zinc-800 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400",
                          "disabled:opacity-40 disabled:cursor-not-allowed",
                        ].join(" ")}
                      >
                        {isPlaying ? "■ stop" : `▶ scene ${i + 1}`}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <textarea
              data-testid="script-edit-narration"
              ref={narrationRef}
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              rows={12}
              maxLength={12000}
              placeholder='Write the spoken narration. Tip: click "+ 1s pause" above to drop a pause at the cursor.'
              className="w-full bg-black border border-zinc-800 focus:border-yellow-400 rounded-sm px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors leading-relaxed resize-y font-mono"
            />
            {/* Karaoke overlay — only visible during marker preview. Each
                word lights up yellow as the narrator reaches it; [[scene]]
                markers render as small dimmed pills so the user can see
                where they sit relative to the narration flow. */}
            {karaokeIdx !== null && (
              <div
                data-testid="karaoke-overlay"
                className="mt-2 bg-zinc-950/80 border border-yellow-400/30 rounded-sm px-3 py-2.5 text-sm leading-relaxed font-mono text-zinc-500"
              >
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400/80 mb-1.5">
                  Karaoke ▸ narrator&apos;s current word
                </div>
                <div>
                  {narrationTokens.map((tok, i) => {
                    if (tok.kind === "marker") {
                      return (
                        <span
                          key={i}
                          className="inline-block bg-yellow-400/10 text-yellow-400/70 border border-yellow-400/30 rounded-sm px-1.5 py-0.5 mx-0.5 text-[10px] uppercase tracking-[0.15em] align-baseline"
                        >
                          ▸ cut
                        </span>
                      );
                    }
                    if (tok.kind === "ws") return tok.text;
                    const active =
                      karaokeIdx >= tok.start && karaokeIdx < tok.end;
                    return (
                      <span
                        key={i}
                        className={
                          active
                            ? "bg-yellow-400/90 text-black px-0.5 rounded-sm transition-colors"
                            : "text-zinc-400 transition-colors"
                        }
                      >
                        {tok.text}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            <NarrationDiff
              oldText={script.narration || ""}
              newText={narration}
            />
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              ~150 wpm · scenes below evenly distribute across this narration
            </p>
          </div>

          {/* Scene breakdown */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1.5">
              Scene visual prompts · {scenes.length}
            </div>
            <div className="space-y-2">
              {scenes.map((s, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-2 border border-zinc-900 rounded-sm p-2"
                  data-testid={`script-edit-scene-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 px-2 py-1.5 rounded-sm shrink-0">
                      Scene {i + 1}
                      {sceneWordTargets[i] ? (
                        <div className="text-[9px] text-zinc-500 mt-0.5 normal-case tracking-normal">
                          ~{sceneWordTargets[i]}w
                        </div>
                      ) : null}
                    </div>
                    <textarea
                      value={s}
                      onChange={(e) => updateScene(i, e.target.value)}
                      rows={2}
                      maxLength={300}
                      placeholder="A wide shot of... (describe the scene like a film director)"
                      className="flex-1 bg-black border border-zinc-800 focus:border-yellow-400 rounded-sm px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors leading-snug resize-y"
                    />
                    {scenes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeScene(i)}
                        title="Remove this scene"
                        data-testid={`script-edit-remove-scene-${i}`}
                        className="shrink-0 font-mono text-[10px] text-zinc-500 hover:text-red-400 px-2 py-1.5 border border-zinc-800 hover:border-red-500/40 rounded-sm transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pl-1">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                      Compose as
                    </span>
                    <SceneModePicker
                      value={sceneModes[i] || "still_static"}
                      onChange={(m) => updateSceneMode(i, m)}
                      sceneIdx={i}
                      compact
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* Add-scene button now lives BELOW the scene list — easier to
                reach after the user has scrolled through existing scenes. */}
            <button
              type="button"
              onClick={addScene}
              data-testid="script-edit-add-scene"
              className="mt-3 w-full font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 border border-dashed border-yellow-400/40 hover:border-yellow-400 hover:bg-yellow-400/5 px-3 py-3 rounded-sm transition-colors"
            >
              + add scene
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2 leading-relaxed">
              tip: describe wide environments &amp; objects · avoid people, hands, faces, text/logos
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ReadonlyScript({ script, voiceover }) {
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-white">
        {script.title || "Untitled"}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {voiceover?.voice_label || "—"} · {script.scene_breakdown?.length || 0} scenes
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap font-mono">
        {script.narration || "(no narration)"}
      </p>
    </div>
  );
}
